import { RemittanceLoanData } from './types';
import { formatCents } from './utils';
import { GetPortfolioCashFlows_user_company_performanceSummary } from 'features/drilldown/cashflows/gql/__generated__/GetPortfolioCashFlows';
import { GetShortCarves_deal_carves_carve_summary } from 'query/__generated__/GetShortCarves';
import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';
import {
  AssetClass,
  DefaultAssumptionType,
  FileState,
  PrepaymentAssumptionType,
  ProductType,
  RateType,
} from '__generated__/globalTypes';

// Mock data for testing the LoanRemittanceProfile component
export const mockLoanRemittanceData = [
  {
    month: 5,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0, // Not displayed but required by MonthlyTable
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 6,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 7,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 8,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 9,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 10,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 11,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
  {
    month: 12,
    starting_balance_cents: 4000000, // $40,000
    payments_cents: 446647, // $4,466.47
    principal_cents: 0,
    interest_cents: 12953, // $129.53
    servicing_cents: 0,
    prepayments_cents: 56597, // $565.97
    defaults_cents: 22991, // $229.91
    losses_cents: 96690, // $966.90
    ending_balance_cents: 0,
    cash_flow_cents: 0,
  },
];

export const mockRemittanceCashflowSummary: GetPortfolioCashFlows_user_company_performanceSummary =
  {
    cashFlows: {
      totals: {
        interest_cents: 120000,
        principal_cents: 340000,
        servicing_cents: 15000,
        prepayments_cents: 25000,
        defaults_minus_losses_cents: -5000,
        subtotal_cents: 495000,
        total_cents: 490000,
      },
      monthlyCashFlows: [
        {
          month: 1,
          cash_flow_cents: 40000,
          starting_balance_cents: 1000000,
          payments_cents: 60000,
          interest_cents: 10000,
          principal_cents: 25000,
          servicing_cents: 2000,
          prepayments_cents: 3000,
          defaults_cents: 500,
          losses_cents: 1000,
          ending_balance_cents: 975000,
        },
        {
          month: 2,
          cash_flow_cents: 38000,
          starting_balance_cents: 975000,
          payments_cents: 58000,
          interest_cents: 9500,
          principal_cents: 24000,
          servicing_cents: 1900,
          prepayments_cents: 2800,
          defaults_cents: 450,
          losses_cents: 900,
          ending_balance_cents: 951000,
        },
      ],
      price_yield_matrix: {
        spread_matrix: null,
        yield_matrix: null,
      },
    },
    unpaidBalance: 951000,
    originalBalance: 1000000,
    loanCount: 12,
    averageBalance: 79250,
    netCoupon: 420,
    wa_servicing_rate: 100,
    averageAge: 18,
    averageMaturity: 42,
    fico: 720,
    dti: 2800,
    ltv: 7000,
    current_balance_cents: 951000,
  };

export const mockRemittanceCarveSummary: GetShortCarves_deal_carves_carve_summary = {
  unpaidPrincipalBalance: 951000,
  cashFlows: {
    totals: {
      interest_cents: 120000,
      principal_cents: 340000,
      servicing_cents: 15000,
      prepayments_cents: 25000,
      defaults_minus_losses_cents: -5000,
      subtotal_cents: 495000,
      total_cents: 490000,
    },
    monthlyCashFlow: [
      {
        month: 1,
        cash_flow_cents: 40000,
        starting_balance_cents: 1000000,
        payments_cents: 60000,
        interest_cents: 10000,
        principal_cents: 25000,
        servicing_cents: 2000,
        prepayments_cents: 3000,
        defaults_cents: 500,
        losses_cents: 1000,
        ending_balance_cents: 975000,
      },
      {
        month: 2,
        cash_flow_cents: 38000,
        starting_balance_cents: 975000,
        payments_cents: 58000,
        interest_cents: 9500,
        principal_cents: 24000,
        servicing_cents: 1900,
        prepayments_cents: 2800,
        defaults_cents: 450,
        losses_cents: 900,
        ending_balance_cents: 951000,
      },
    ],
    price_yield_matrix: {
      spread_matrix: null,
      yield_matrix: null,
    },
  },
  originalBalance: 1000000,
  loanCount: 12,
  averageBalance: 79250,
  averageOriginalBalance: 83333,
  netCoupon: 420,
  servicingRate: 100,
  averageAge: 18,
  averageMaturity: 42,
  fico: 720,
  dti: 2800,
  ltv: 7000,
};

export const mockRemittanceCashflowFormValues: CashFlowsValidValues = {
  cdr: 0,
  cpr: 0,
  lsr: 0,
  daysDelay: 0,
  servicingRate: 0,
  rateType: RateType.Sofr,
  defaultAssumptionType: DefaultAssumptionType.CDR,
  prepaymentAssumptionType: PrepaymentAssumptionType.CPR,
};

export type RemittanceMetricTone = 'positive' | 'negative' | undefined;
export type RemittanceMetricItem = {
  label: string;
  value: string;
  valueTone?: RemittanceMetricTone;
};

/** Default metrics when no deal data (single column). */
export const mockRemittanceMetrics: RemittanceMetricItem[] = [
  { label: 'Total cash', value: '$0' },
  { label: 'Principal', value: '$0' },
  { label: 'Interest', value: '$0' },
  { label: 'Servicing fee', value: '$0', valueTone: 'negative' },
];

export type RemittanceTotalsCents = {
  principal_cents: number;
  interest_cents: number;
  servicing_cents: number;
  prepayments_cents?: number;
  subtotal_cents?: number;
  total_cents: number;
};

/**
 * Builds a single list of remittance metrics from deal-level totals.
 * Uses principal + interest + servicing from getDealRemittanceSummaries (currently participation/investor-share only).
 * For a true investor vs seller split we need total_*_full_cents in the summary (and participation rate); see REMITTANCE_METRICS_BACKEND.md.
 */
export function buildRemittanceMetricsFromTotals(
  totals: RemittanceTotalsCents,
): RemittanceMetricItem[] {
  const principal = totals.principal_cents ?? 0;
  const interest = totals.interest_cents ?? 0;
  const servicing = totals.servicing_cents ?? 0;
  const totalCash = totals.total_cents ?? principal + interest + servicing;
  return [
    { label: 'Total cash', value: formatCents(totalCash) },
    { label: 'Principal', value: formatCents(principal) },
    { label: 'Interest', value: formatCents(interest) },
    { label: 'Servicing fee', value: `-${formatCents(servicing)}`, valueTone: 'negative' as const },
  ];
}

export const mockRemittanceWireInfo = {
  bankIdTypeOptions: [
    { value: 'credit_union', label: 'Credit Union' },
    { value: 'bank', label: 'Bank' },
  ],
  investor: {
    bankIdType: 'credit_union',
    bankId: '12345-54',
    bankName: 'Name',
    bankAddress1: '123 Main Street',
    bankAddress2: 'Suite 100',
    bankCityCountry: 'City, USA',
  },
  seller: {
    bankIdType: 'credit_union',
    bankId: '12345-54',
    accountNumber: '',
    originatorName: 'Name',
    originatorAddress1: '123 Main Street',
    originatorAddress2: 'Suite 100',
    originatorCityCountryPostal: 'City, USA, POSTAL CODE',
  },
  servicer: {
    bankIdType: 'credit_union',
    bankId: '12345-54',
    originatorName: 'Name',
    originatorAddress1: '123 Main Street',
    originatorAddress2: 'Suite 100',
    originatorCityCountry: 'City, USA, POSTAL CODE',
  },
  securityCode: '1234567',
} as const;

// Mock data for the remittance tape deal table
export const mockRemittanceTapeDealData: RemittanceLoanData[] = [
  {
    id: 'LOAN-001',
    account_id: 'ACC-001',
    current_balance_cents: 25000000, // $250,000
    original_balance_cents: 30000000, // $300,000
    payStringData: {
      '2022': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'L', month: 9 },
        { letter: 'L', month: 10 },
        { letter: 'L', month: 11 },
        { letter: 'L', month: 12 },
      ],
      '2023': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'L', month: 9 },
        { letter: 'L', month: 10 },
        { letter: 'L', month: 11 },
        { letter: 'L', month: 12 },
      ],
      '2024': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'L', month: 9 },
      ],
    },
    maturity_date: '2025-12-31',
    interest_rate: 4.5,
    accrued_unpaid_interest_cents: 125075, // $1,250.75
  },
  {
    id: 'LOAN-002',
    account_id: 'ACC-002',
    current_balance_cents: 18000000, // $180,000
    original_balance_cents: 20000000, // $200,000
    payStringData: {
      '2022': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'D', month: 7 },
        { letter: 'C', month: 8 },
        { letter: 'C', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2023': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'D', month: 8 },
        { letter: 'C', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2024': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'D', month: 6 },
        { letter: 'C', month: 7 },
        { letter: 'C', month: 8 },
        { letter: 'C', month: 9 },
      ],
    },
    maturity_date: '2026-06-15',
    interest_rate: 3.875,
    accrued_unpaid_interest_cents: 69833, // $698.33
  },
  {
    id: 'LOAN-003',
    account_id: 'ACC-003',
    current_balance_cents: 42500000, // $425,000
    original_balance_cents: 45000000, // $450,000
    payStringData: {
      '2022': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'D', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2023': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'X', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'D', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2024': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'X', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'D', month: 7 },
        { letter: 'C', month: 8 },
        { letter: 'C', month: 9 },
      ],
    },
    maturity_date: '2027-03-20',
    interest_rate: 5.125,
    accrued_unpaid_interest_cents: 218750, // $2,187.50
  },
  {
    id: 'LOAN-004',
    account_id: 'ACC-004',
    current_balance_cents: 31500000, // $315,000
    original_balance_cents: 35000000, // $350,000
    payStringData: {
      '2022': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'D', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2023': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'D', month: 7 },
        { letter: 'C', month: 8 },
        { letter: 'C', month: 9 },
        { letter: 'C', month: 10 },
        { letter: 'C', month: 11 },
        { letter: 'C', month: 12 },
      ],
      '2024': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'D', month: 7 },
        { letter: 'C', month: 8 },
        { letter: 'C', month: 9 },
      ],
    },
    maturity_date: '2025-09-10',
    interest_rate: 4.25,
    accrued_unpaid_interest_cents: 134375, // $1,343.75
  },
  {
    id: 'LOAN-005',
    account_id: 'ACC-005',
    current_balance_cents: 55000000, // $550,000
    original_balance_cents: 60000000, // $600,000
    payStringData: {
      '2022': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'L', month: 9 },
        { letter: 'L', month: 10 },
        { letter: 'L', month: 11 },
        { letter: 'L', month: 12 },
      ],
      '2023': [
        { letter: 'L', month: 1 },
        { letter: 'L', month: 2 },
        { letter: 'L', month: 3 },
        { letter: 'L', month: 4 },
        { letter: 'L', month: 5 },
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'L', month: 8 },
        { letter: 'L', month: 9 },
        { letter: 'L', month: 10 },
        { letter: 'L', month: 11 },
        { letter: 'L', month: 12 },
      ],
      '2024': [
        { letter: 'L', month: 6 },
        { letter: 'L', month: 7 },
        { letter: 'D', month: 8 },
        { letter: 'C', month: 9 },
      ],
    },
    maturity_date: '2028-01-25',
    interest_rate: 4.75,
    accrued_unpaid_interest_cents: 262500, // $2,625.00
  },
];

export const mockSelectedFile = {
  __typename: 'File',
  id: 'test_remittance_file_id',
  name: 'test_remittance_file.xlsx',
  state: FileState.NEEDS_MAPPING,
  assetClass: AssetClass.HOME,
  productType: ProductType.FIXED,
  createdDate: '2025-12-10T00:44:10.378Z',
  original_id: 'original_test_remittance_file_id',
  uploaded_by: {
    __typename: 'User',
    id: '256b8c9d-8c99-5093-b692-b295bd66728a',
    firstName: 'Y',
    lastName: 'J',
  },
  processingReport: {
    __typename: 'ProcessingReport',
    new_count: null,
    flagged_count: null,
    duplicate_count: null,
    duplicate_records_csv_url: null,
    duplicate_records_excel_url: null,
    failed_count: null,
    failed_records_csv_url: null,
    failed_records_excel_url: null,
    flagged_records_csv_url: null,
    flagged_records_excel_url: null,
    new_records_csv_url: null,
    new_records_excel_url: null,
  },
  processing_percent: null,
  total_persist_count: null,
  persisted_count: null,
  persisting_percent: null,
  headers: [
    'Mortgage Loan ID',
    'Product Type',
    'Loan Purpose',
    'Current Note Rate',
    'Original Loan Balance',
    'Current Loan Balance',
    'Origination Date',
    'First Pmt Date',
    'Maturity Date',
    'Original LTV',
    'Qualifying FICO',
    'DTI',
    'Occupancy',
    'Property Type',
    'Property City',
    'Property State',
    'Next Payment Date',
    'Servicing Rate',
  ],
  field_mapping: [],
};
